/**
 * useShellAreaByHand - who an area attack catches, answered from a tick list.
 *
 * The hand-ruled twin of `useShellAreaTargeting`, used while positions do
 * not count. It returns the same three callbacks with the same shapes, so
 * the attack cannot tell which one it was handed:
 *
 * - `resolveVictims(slot, primaryTabId, scatter, rolled)` - null when the
 *   shot is not an area one or nobody has answered; else the ticked victims,
 *   an arc's in the order ticked (the order IS the leaps).
 * - `resolveSpawned(slot, spawnerIds, spawn, struckIds)` - the ticked
 *   secondary victims whose spawner really spawned.
 * - `suppressionZone()` - the ticked ids: who is shot and who keeps their
 *   head down are the same people, as on the map.
 *
 * Victims are projected by `projectVictimList` with nobody on the map: no
 * cover from terrain, no range band of their own (the attack's hand-picked
 * band applies), which is what "set by hand" means.
 *
 * `ticksRef` holds the current answer (`engine/phase/areaTicks`). The shell
 * writes it just before it fires; everything here reads it at call time,
 * and the roster too, so a detonation resumed later sees today's people.
 */
import { useCallback, useMemo, useRef } from 'react';
import { leapsOf, spawnedFor } from '../engine/phase/areaTicks';
import { EMPTY_SOLIDS, isAreaShot, projectVictimList, rolledShape } from './useShellAreaTargeting';

const NO_TOKENS = Object.freeze({});

/** The victims for a list of ids, each with the leap the ticks give them. */
function victimsWithLeaps(project, slot, ticks, isArc) {
  const leaps = leapsOf(ticks, isArc);
  return project(slot, ticks.caught).map((v) => ({ ...v, leap: leaps.get(v.tabId) ?? 0 }));
}

/**
 * @param {object} args
 * @param {object[]} args.roster
 * @param {string|null} args.actorId
 * @param {string} args.coverConflictMode
 * @param {string|null} args.attackMode - 'suppressing' for one shot, else null
 * @param {{current: object}} args.surfaceEffectsRef
 */
export default function useShellAreaByHand({ roster, actorId, coverConflictMode, attackMode, surfaceEffectsRef }) {
  const ticksRef = useRef(null);
  const live = useRef({ roster, actorId, coverConflictMode, attackMode });
  live.current = { roster, actorId, coverConflictMode, attackMode };

  const project = useCallback(
    (slot, ids) => {
      const { roster: people, actorId: shooter, coverConflictMode: conflict } = live.current;
      const field = { tokens: NO_TOKENS, roster: people, actorId: shooter, solids: EMPTY_SOLIDS, isGm: false };
      return projectVictimList(
        { ...field, derived: false, coverConflictMode: conflict, surfaceEffects: surfaceEffectsRef.current },
        slot,
        ids,
      );
    },
    [surfaceEffectsRef],
  );

  const resolveVictims = useCallback(
    (slot, _primaryTabId, _scatter, rolled) => {
      const ticks = ticksRef.current;
      const shape = rolledShape(rolled);
      if (!ticks || !isAreaShot(shape, live.current.attackMode === 'suppressing')) return null;
      return victimsWithLeaps(project, slot, ticks, shape.arcActive);
    },
    [project],
  );

  const resolveSpawned = useCallback(
    (slot, spawnerIds, _spawn, struckIds) => {
      const ticks = ticksRef.current;
      if (!ticks) return null;
      const spawned = spawnedFor(ticks, spawnerIds, struckIds);
      const from = new Map(spawned.map((s) => [s.id, s.from]));
      return project(
        slot,
        spawned.map((s) => s.id),
      ).map((v) => ({ ...v, leap: 0, spawnedFrom: from.get(v.tabId) }));
    },
    [project],
  );

  const suppressionZone = useCallback(() => ticksRef.current?.caught ?? [], []);

  return useMemo(
    () => ({ ticksRef, resolveVictims, resolveSpawned, suppressionZone }),
    [resolveVictims, resolveSpawned, suppressionZone],
  );
}
